import { Component, EventEmitter, Input, Output } from "@angular/core";

/**
 * The six macro steps of the MAIA flow. Only "upload" and "review" are
 * reachable in Phase 1 — the rest render as upcoming/disabled until their
 * screens are built.
 */
export type MaiaFlowStepId = "upload" | "review" | "verify" | "enrich" | "analyze" | "save";

interface MaiaFlowStep {
    id: MaiaFlowStepId;
    label: string;
}

/**
 * Full-width bar sitting below the app's own <map-header> and above the
 * MAIA body: Exit, "Upload New File", and the six-step flow indicator.
 *
 * Design reference: attachment shared in review — step labels are provisional
 * and may be renamed without changing this component's shape.
 */
@Component({
    selector: "maia-step-header",
    templateUrl: "./maia-step-header.component.html",
    styleUrls: ["./maia-step-header.component.scss"],
})
export class MaiaStepHeaderComponent {
    @Input() currentStep: MaiaFlowStepId = "upload";

    /**
     * Set once the portfolio has actually finished importing. Without this,
     * "Save to MAPTYCS" — the last step — could never show as done: `isDone`
     * only checks position relative to the current step, and there's no step
     * after the last one to have moved past it.
     */
    @Input() allStepsDone = false;

    @Output() readonly exitRequested = new EventEmitter<void>();

    /**
     * "Upload New File" (was "Upload new SOV file"). Lived on the chat panel until that was hidden;
     * the page handles it exactly as before (MaiaPageComponent.openUploadPanel),
     * so starting a fresh upload still prompts about unsaved progress first.
     */
    @Output() readonly uploadRequested = new EventEmitter<void>();

    readonly steps: MaiaFlowStep[] = [
        { id: "upload", label: "Upload File" },
        { id: "review", label: "Review Data" },
        { id: "verify", label: "Verify Locations" },
        { id: "enrich", label: "Enrich Data" },
        { id: "analyze", label: "Analyze & Report" },
        { id: "save", label: "Save to MAPTYCS" },
    ];

    private get currentIndex(): number {
        return this.steps.findIndex((step) => step.id === this.currentStep);
    }

    isDone(stepId: MaiaFlowStepId): boolean {
        if (this.allStepsDone) {
            return true;
        }
        const index = this.steps.findIndex((step) => step.id === stepId);
        return index >= 0 && index < this.currentIndex;
    }

    isActive(stepId: MaiaFlowStepId): boolean {
        return !this.allStepsDone && stepId === this.currentStep;
    }
}
